import * as Api from '@ViewModels';
import { useInfiniteQuery, useQueryClient, type InfiniteData } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo } from 'react';
import { useUserSession } from '../../models/hooks/appStateHooks';
import { LevStandardQueryMeta, reactQueryClient } from '../../web/ReactQueryProvider';

const BASE_QUERY_KEY = '/users';

export interface UseLoadAllUsersOptions {
	pageSize?: number;
	impersonationContext?: Api.IImpersonationContext;
	meta?: LevStandardQueryMeta;
}

export interface UseLoadAllUsersResult {
	users: Api.IUser[];
	isLoading: boolean;
	error: Error | null;
	updateUser: (updatedUser: Api.IUser) => void;
}

/**
 * Hook for loading all users using direct API calls with pagination.
 * This bypasses React Query's infinite query limitations by implementing
 * the same pagination logic used in the admin panel.
 */
export function useLoadAllUsers({
	pageSize = 500,
	impersonationContext,
	meta,
}: UseLoadAllUsersOptions = {}): UseLoadAllUsersResult {
	const userSession = useUserSession();
	const queryClient = useQueryClient();
	const queryKey = useMemo(
		() => [BASE_QUERY_KEY, pageSize, impersonationContext] as const,
		[pageSize, impersonationContext]
	);
	const query = useInfiniteQuery<Api.IPagedCollection<Api.IUser>, Error>({
		queryKey,
		queryFn: ({ pageParam }) => {
			return userSession.webServiceHelper.callAsync<Api.IPagedCollection<Api.IUser>>(
				Api.ImpersonationBroker.composeApiUrl({
					impersonationContext,
					queryParams: { pageSize, pageToken: pageParam },
					urlPath: `user/`,
				}),
				'GET',
				null
			);
		},
		getNextPageParam: lastPage => lastPage.pageToken,
		initialPageParam: '',
		staleTime: 10 * 60 * 1000,
		meta,
	});

	const { isFetching, hasNextPage, fetchNextPage } = query;

	useEffect(() => {
		if (hasNextPage && !isFetching) {
			fetchNextPage();
		}
	}, [hasNextPage, isFetching, fetchNextPage]);

	const users = useMemo(() => {
		return query.data?.pages.flatMap(page => page.values ?? []) ?? [];
	}, [query.data]);

	const updateUser = useCallback(
		(updatedUser: Api.IUser) => {
			queryClient.setQueryData<InfiniteData<Api.IPagedCollection<Api.IUser>> | undefined>(queryKey, currentData => {
				if (!currentData) {
					return currentData;
				}

				const updatedPages = currentData.pages.map(page => {
					if (!page.values?.length) {
						return page;
					}

					return {
						...page,
						values: page.values.map(user => (user.id === updatedUser.id ? updatedUser : user)),
					};
				});

				return {
					...currentData,
					pages: updatedPages,
				};
			});
		},
		[queryClient, queryKey]
	);

	return {
		users,
		isLoading: query.isLoading || (query.isFetching && query.hasNextPage),
		error: query.error ?? null,
		updateUser,
	};
}

export function invalidateAllUsers() {
	reactQueryClient.invalidateQueries({ queryKey: [BASE_QUERY_KEY] });
}
